import React, { FC, HTMLAttributes, SyntheticEvent, useEffect, useMemo } from 'react';

import PageTitle from '@ui/PageTitle';
import { Box, Grid, Tooltip } from '@mui/material';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import PageSubtitle from '@ui/PageSubtitle';
import Autocomplete from '@mui/material/Autocomplete';
import { StyledSelectTitle } from '@ui/Select/styled';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { useGetCoursesQuery } from '../../../../api/bookTicket.api';
import StepperButtons from '../StepperButtons';
import {
  FeeInfo,
  OptionTooltip, OptionTooltipButton,
  OptionTooltipContent,
  OptionTooltipFee,
  OptionTooltipTitle,
  PickCourseContainer,
  ReviewBox,
  ReviewBoxCourseItem,
  ReviewBoxTitle,
  Selects,
  StyledPopper,
  MenuItem, ReviewBoxCourseTitle,
} from './styled';
import {
  pickCourseFormValidationResolver,
  savePickCourseDataInStorage,
  sortCoursesByName,
} from '../../../../helpers/pickCourseHelpers';
import { ICourse, IPickCourseFormValues, ISelectDateFormValues, IClosure } from '../../../../types';
import questionIcon from '@assets/icons/question.svg';
import { range } from '@utils/array';
import { AutocompleteTextField } from '../../styled';

dayjs.extend(utc);
dayjs.extend(timezone);

interface PickCourseStepProps {
  defaultValues?: IPickCourseFormValues;
  onNext: (data: IPickCourseFormValues) => void;
  dateInfo: ISelectDateFormValues;
  onBack: () => void;
}

function combineDateAndTime(dateInput: string | Date | dayjs.Dayjs, timeStr: string) {
  const [hours, minutesPart] = timeStr.split(':');
  const [minutes, period] = minutesPart.split(' ');
  let hour = parseInt(hours, 10);

  if (period === 'PM' && hour !== 12) {
    hour += 12;
  }

  if (period === 'AM' && hour === 12) {
    hour = 0;
  }

  const base = dayjs.tz(dateInput, 'America/New_York')
    .set('hour', hour)
    .set('minute', parseInt(minutes, 10))
    .set('second', 0)
    .set('millisecond', 0);

  return base.format('YYYY-MM-DD HH:mm:ss.SSS');
}

const isCourseCurrentlyClosed = (date: Date, closures: IClosure[] = []): boolean => {
  const now = dayjs.tz(date, 'America/New_York');

  return closures.some(({ startDate, endDate }) => {
    const start = dayjs.tz(startDate, 'America/New_York');
    const end = dayjs.tz(endDate, 'America/New_York');

    return ((now.isAfter(start) && now.isBefore(end)) || now.isSame(start) || now.isSame(end));
  });
};

const isDateAllowedBySchedule = (date: Date, schedule: string[]): boolean => {
  const dayOfWeek = dayjs(date).format('dddd').toUpperCase();

  return schedule.includes(dayOfWeek);
};

const isOptionEqualToValue = (a: ICourse, b: ICourse) => a.id === b.id;

const optionRenderer = (date: Date) => (
  props: HTMLAttributes<HTMLLIElement>,
  option: ICourse,
) => {
  const { id, name, memberFee, guestFee, closures = [], schedule = [] } = option;
  const now = dayjs.tz(date, 'America/New_York');

  const activeClosure = closures.find(({ startDate, endDate }) => {
    const start = dayjs.tz(startDate, 'America/New_York');
    const end = dayjs.tz(endDate, 'America/New_York');

    return ((now.isAfter(start) && now.isBefore(end)) || now.isSame(start) || now.isSame(end));
  });

  const notInSchedule = !isDateAllowedBySchedule(date, schedule);

  let tooltipTitle = '';

  if (activeClosure) {
    tooltipTitle = `Course is closed until ${dayjs(activeClosure.endDate).format('MMM D, YYYY')}`;
  } else if (notInSchedule) {
    tooltipTitle = `Course is not available on ${dayjs(date).format('dddd')}`;
  }

  const isDisabled = (!!activeClosure) || notInSchedule;

  return (
    <Tooltip title={tooltipTitle} arrow placement="left">
      <span>
        <MenuItem
          {...props}
          key={id}
          disabled={isDisabled}
          value={id}
          data-testid="course-option"
        >
          {name}
          <OptionTooltip
            onClick={(e) => e.stopPropagation()}
            enterTouchDelay={0}
            title={
              (
                <OptionTooltipContent>
                  <OptionTooltipTitle>Price</OptionTooltipTitle>
                  <OptionTooltipFee>{`Member $${memberFee}`}</OptionTooltipFee>
                  <OptionTooltipFee>{`Guest $${guestFee}`}</OptionTooltipFee>
                </OptionTooltipContent>
              )
            }
            placement="top"
          >
            <OptionTooltipButton>
              <img src={questionIcon} alt="question" />
            </OptionTooltipButton>
          </OptionTooltip>
        </MenuItem>
      </span>
    </Tooltip>
  );
};

const PickCourseStep: FC<PickCourseStepProps> = ({ onNext, defaultValues, dateInfo, onBack }) => {
  const pickedDate = dateInfo?.date ? new Date(combineDateAndTime(dateInfo.date, dateInfo.time)) : new Date();

  const { handleSubmit, control, formState: { errors }, trigger } = useForm<IPickCourseFormValues>({
    resolver: pickCourseFormValidationResolver,
    defaultValues,
    reValidateMode: 'onChange',
  });

  const { fields, remove, update } = useFieldArray({
    control,
    name: 'courses',
    keyName: 'customID',
  });

  const coursesValues = useWatch({
    control,
    name: 'courses',
  });

  const [firstCourse, secondCourse, thirdCourse] = coursesValues;

  const { data: courses } = useGetCoursesQuery();

  const sortedCourses = useMemo(
    () => sortCoursesByName(courses?.data || []),
    [courses?.data],
  );

  const secondCourseOptions = useMemo(() => {
    return sortedCourses.filter(({ id }) => id !== firstCourse?.id);
  }, [sortedCourses, firstCourse?.id]);

  const thirdCourseOptions = useMemo(() => {
    return secondCourseOptions.filter(({ id }) => id !== secondCourse?.id);
  }, [secondCourseOptions, secondCourse?.id]);

  const fourthCourseOptions = useMemo(() => {
    return thirdCourseOptions.filter(({ id }) => id !== thirdCourse?.id);
  }, [thirdCourseOptions, thirdCourse?.id]);

  const handleSelectChange = (index: number) => (
    event: SyntheticEvent,
    newValue: ICourse | null,
  ) => {
    if (!newValue) {
      return;
    }

    remove(range(index + 1, fields.length));

    update(index, newValue);

    trigger(`courses.${index}`);
  };

  const inputsToRender = useMemo(() => {
    return [
      {
        name: 'firstCourse',
        placeholder: 'Choose Course',
        title: 'First Priority*',
        options: sortedCourses,
      },
      {
        name: 'secondCourse',
        placeholder: 'Choose Course',
        title: 'Second Priority*',
        options: secondCourseOptions as ICourse[],
      },
      {
        name: 'thirdCourse',
        placeholder: 'Choose Course',
        title: 'Third Priority',
        options: thirdCourseOptions as ICourse[],
      },
      {
        name: 'fourthCourse',
        placeholder: 'Choose Course',
        title: 'Fourth Priority',
        options: fourthCourseOptions as ICourse[],
      },
    ];
  }, [sortedCourses, fourthCourseOptions, secondCourseOptions, thirdCourseOptions]);

  useEffect(() => {
    savePickCourseDataInStorage({ courses: coursesValues });
  }, [coursesValues]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PickCourseContainer
      component="form"
      onSubmit={handleSubmit(onNext)}
    >
      <PageTitle textAlign="center">Pick 2-4 courses</PageTitle>
      <PageSubtitle textAlign="center">
        In priority order select a
        minimum of 2 courses you are willing to play.
      </PageSubtitle>

      <Box className="courses-wrapper">
        <Selects container>
          {inputsToRender.map((input, index) => {
            const disabled = index !== 0 && !fields[index - 1];

            return (
              <Grid item lg="auto" xs={12} key={input.name}>
                <Box>
                  <StyledSelectTitle>
                    {input.title}
                  </StyledSelectTitle>
                  <Autocomplete
                    data-testid={`course-select-${index}`}
                    aria-disabled={disabled}
                    noOptionsText="No courses"
                    PopperComponent={StyledPopper}
                    value={fields[index] || null}
                    title={input.title}
                    onChange={handleSelectChange(index)}
                    isOptionEqualToValue={isOptionEqualToValue}
                    id={input.title}
                    options={input.options || []}
                    getOptionLabel={(option) => option.name}
                    getOptionDisabled={(option) => isCourseCurrentlyClosed(pickedDate, option.closures)}
                    renderOption={optionRenderer(pickedDate)}
                    disabled={disabled}
                    disablePortal
                    renderInput={(params) => (
                      <AutocompleteTextField
                        {...params}
                        error={!!errors?.courses?.[index]?.message}
                        helperText={errors?.courses?.[index]?.message}
                        placeholder={input.placeholder}
                      />
                    )}
                    disableClearable
                  />
                </Box>

              </Grid>
            );
          })}
        </Selects>
      </Box>

      {!!fields.length && (
        <Box>
          <ReviewBox>
            <ReviewBoxTitle>
              Selected courses
            </ReviewBoxTitle>

            {fields.map((item, index) => {
              return (
                <ReviewBoxCourseItem key={item.id}>
                  <Box>
                    <span>{`${index + 1}.`}</span>
                  </Box>
                  <Box>
                    <ReviewBoxCourseTitle>
                      <span>{item.name}</span>
                      <FeeInfo>
                        {`(Member $${item.memberFee}, Guest $${item.guestFee})`}
                      </FeeInfo>
                    </ReviewBoxCourseTitle>
                    {item.notes && <FeeInfo>{item.notes}</FeeInfo>}
                  </Box>
                </ReviewBoxCourseItem>
              );
            })}
          </ReviewBox>
        </Box>
      )}

      <StepperButtons
        backButtonProps={{
          onClick: () => {
            onBack();
          },
        }}
        nextButtonProps={{
          type: 'submit',
        }}
      />
    </PickCourseContainer>
  );
};

PickCourseStep.defaultProps = {
  defaultValues: {
    courses: [],
  },
};

export default PickCourseStep;
